
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Maximize2, Minimize2, Pause, Play, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { disableYoutubeCaptionsCommands } from '@shared/media/youtube/youtube-embed-url'
import { MediaControlBar } from '@shared/media/media-control-bar'
import { AnchoredPopover, LmsButton } from '@shared/ui'

import {
  getLegacyYoutubeEmbedUrl,
  getYoutubeThumbnailUrl,
  parseYoutubeMessage,
} from './legacy-youtube-utils'

type LegacyYoutubeSettingsSection = 'menu' | 'speed'
type LegacyYoutubePlaybackRate = 0.75 | 1

function LegacyYoutubeSettings({
  anchorEl,
  onChangeSection,
  onChangeSpeed,
  onClose,
  opensUp = false,
  playbackRate,
  section,
}: {
  readonly anchorEl: HTMLButtonElement | null
  readonly onChangeSection: (section: LegacyYoutubeSettingsSection) => void
  readonly onChangeSpeed: (playbackRate: LegacyYoutubePlaybackRate) => void
  readonly onClose: () => void
  readonly opensUp?: boolean
  readonly playbackRate: LegacyYoutubePlaybackRate
  readonly section: LegacyYoutubeSettingsSection
}) {
  const { t } = useTranslation('book')
  const speedLabel = t('Speed')
  const normalLabel = t('Normal')
  const speedText = playbackRate === 1 ? normalLabel : `${playbackRate}x`

  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      anchorOrigin={
        opensUp
          ? { horizontal: 'right', vertical: 'top' }
          : { horizontal: 'left', vertical: 'bottom' }
      }
      className="legacyAudioSettingsPopoverPaper legacyAudioSettingsPopover legacySelfStudyYoutubeSettingsPopover"
      disablePortal
      onClose={onClose}
      open={Boolean(anchorEl)}
      transformOrigin={
        opensUp
          ? { horizontal: 'right', vertical: 'bottom' }
          : { horizontal: 'left', vertical: 'top' }
      }
      transitionDuration={0}
    >
      <div className="boxSpeed legacySourceAudioSettingsBoxSpeed legacySelfStudyAudioSettingsPanel legacySelfStudyYoutubeSettingsPanel">
        {section === 'speed' ? (
          <div>
            <LmsButton
              className="btn-details legacyAudioSettingBack"
              onClick={() => onChangeSection('menu')}
              type="button"
              variant="ghost"
            >
              <span className="btn-content">{speedLabel}</span>
            </LmsButton>
            <div className="menu legacyAudioSettingMenu">
              {([0.75, 1] as const).map((rate) => (
                <LmsButton
                  className={`btnMenu legacyAudioSettingOption${playbackRate === rate ? ' current' : ''}`}
                  key={rate}
                  onClick={() => onChangeSpeed(rate)}
                  type="button"
                  variant="ghost"
                >
                  <span>{rate === 1 ? normalLabel : `${rate}x`}</span>
                </LmsButton>
              ))}
            </div>
          </div>
        ) : null}
        <div style={{ display: section === 'menu' ? 'block' : 'none' }}>
          <div className="menu legacyAudioSettingMenu">
            <LmsButton
              className="btn legacyAudioSettingDrill"
              onClick={() => onChangeSection('speed')}
              type="button"
              variant="ghost"
            >
              <span className="btn-content legacyAudioSettingDrillContent">
                {speedLabel}
                <span className="btn-content-value legacyAudioSettingDrillValue">
                  {speedText}
                </span>
              </span>
            </LmsButton>
          </div>
        </div>
      </div>
    </AnchoredPopover>
  )
}

export function LegacyYoutubePlayer({
  endAt,
  startAt = 0,
  title = 'Self-study video',
  youtubeId,
}: {
  readonly endAt?: number
  readonly startAt?: number
  readonly title?: string
  readonly youtubeId: string
}) {
  const { t } = useTranslation('book')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const mediaRootRef = useRef<HTMLDivElement | null>(null)
  const [duration, setDuration] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playedSeconds, setPlayedSeconds] = useState(startAt)
  const [playbackRate, setPlaybackRate] = useState<LegacyYoutubePlaybackRate>(1)
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLButtonElement | null>(null)
  const [settingsSection, setSettingsSection] = useState<LegacyYoutubeSettingsSection>('menu')
  const hasSuppressedCaptionsRef = useRef(false)
  const embedUrl = useMemo(
    () => getLegacyYoutubeEmbedUrl(youtubeId, startAt),
    [startAt, youtubeId],
  )
  const effectiveEnd = endAt && endAt > startAt ? endAt : duration
  const playableDuration = Math.max((effectiveEnd || duration || startAt + 1) - startAt, 1)
  const progress = Math.min(
    100,
    Math.max(0, ((playedSeconds - startAt) / playableDuration) * 100),
  )
  const thumbnailUrl = getYoutubeThumbnailUrl(youtubeId)

  useEffect(() => {
    hasSuppressedCaptionsRef.current = false
  }, [embedUrl])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    const handleFullscreenChange = () => {
      setIsExpanded(document.fullscreenElement === mediaRootRef.current)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  useEffect(() => {
    if (!isExpanded || typeof document === 'undefined') {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && document.fullscreenElement !== mediaRootRef.current) {
        setIsExpanded(false)
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isExpanded])

  const postCommand = useCallback((func: string, args: readonly unknown[] = []) => {
    const targetWindow = iframeRef.current?.contentWindow

    if (!targetWindow) {
      return
    }

    targetWindow.postMessage(JSON.stringify({ args, event: 'command', func }), '*')
  }, [])
  const disableYoutubeCaptions = useCallback(() => {
    const targetWindow = iframeRef.current?.contentWindow

    if (!targetWindow) {
      return
    }

    const postDisableCommands = () => {
      for (const command of disableYoutubeCaptionsCommands) {
        targetWindow.postMessage(JSON.stringify(command), '*')
      }
    }

    postDisableCommands()
    window.setTimeout(postDisableCommands, 250)
    window.setTimeout(postDisableCommands, 1000)
    hasSuppressedCaptionsRef.current = true
  }, [])

  const handleTogglePlayback = useCallback(() => {
    if (isPlaying) {
      postCommand('pauseVideo')
      setIsPlaying(false)
      return
    }

    postCommand('playVideo')
    disableYoutubeCaptions()
    setIsPlaying(true)
  }, [disableYoutubeCaptions, isPlaying, postCommand])

  const handleSeekPercent = useCallback(
    (value: number) => {
      const nextSecond = startAt + (playableDuration * value) / 100
      setPlayedSeconds(nextSecond)
      postCommand('seekTo', [nextSecond, true])
    },
    [playableDuration, postCommand, startAt],
  )

  const handleChangeSpeed = useCallback(
    (rate: LegacyYoutubePlaybackRate) => {
      setPlaybackRate(rate)
      postCommand('setPlaybackRate', [rate])
    },
    [postCommand],
  )

  const handleEnterExpanded = useCallback(() => {
    const mediaRoot = mediaRootRef.current

    setSettingsAnchor(null)
    setSettingsSection('menu')
    setIsExpanded(true)

    if (!mediaRoot || typeof mediaRoot.requestFullscreen !== 'function') {
      return
    }

    void mediaRoot.requestFullscreen().catch(() => {
      setIsExpanded(true)
    })
  }, [])

  const handleExitExpanded = useCallback(() => {
    const mediaRoot = mediaRootRef.current

    setSettingsAnchor(null)
    setSettingsSection('menu')

    if (
      typeof document !== 'undefined' &&
      document.fullscreenElement === mediaRoot &&
      typeof document.exitFullscreen === 'function'
    ) {
      void document.exitFullscreen().catch(() => {
        setIsExpanded(false)
      })
      return
    }

    setIsExpanded(false)
  }, [])

  const handleToggleExpanded = useCallback(() => {
    if (isExpanded) {
      handleExitExpanded()
      return
    }

    handleEnterExpanded()
  }, [handleEnterExpanded, handleExitExpanded, isExpanded])

  const handleIframeLoad = useCallback(() => {
    const targetWindow = iframeRef.current?.contentWindow

    if (!targetWindow) {
      return
    }

    targetWindow.postMessage(JSON.stringify({ event: 'listening' }), '*')
    disableYoutubeCaptions()
    postCommand('seekTo', [startAt, true])
    postCommand('setPlaybackRate', [playbackRate])
  }, [disableYoutubeCaptions, playbackRate, postCommand, startAt])

  useEffect(() => {
    const handleYoutubeMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return
      }

      const payload = parseYoutubeMessage(event.data)

      if (!payload) {
        return
      }

      if (payload.event === 'onApiChange') {
        disableYoutubeCaptions()
        return
      }

      if (payload.event === 'onReady') {
        disableYoutubeCaptions()
        postCommand('seekTo', [startAt, true])
        postCommand('setPlaybackRate', [playbackRate])
        return
      }

      if (payload.event === 'infoDelivery' && payload.info) {
        if (!hasSuppressedCaptionsRef.current) {
          disableYoutubeCaptions()
        }

        const { currentTime, duration: nextDuration, playerState } = payload.info

        if (typeof nextDuration === 'number' && Number.isFinite(nextDuration)) {
          setDuration(nextDuration)
        }

        if (typeof currentTime === 'number' && Number.isFinite(currentTime)) {
          setPlayedSeconds(currentTime)

          if (effectiveEnd && currentTime >= effectiveEnd - 0.15) {
            postCommand('seekTo', [startAt, true])

            postCommand('playVideo')

            disableYoutubeCaptions()

            setPlayedSeconds(startAt)
          }
        }

        if (playerState === 1) {
          setIsPlaying(true)
        } else if (playerState === 0 || playerState === 2 || playerState === 5) {
          setIsPlaying(false)
        }
      }
    }

    window.addEventListener('message', handleYoutubeMessage)

    return () => {
      window.removeEventListener('message', handleYoutubeMessage)
    }
  }, [disableYoutubeCaptions, effectiveEnd, playbackRate, postCommand, startAt])

  if (!embedUrl) {
    return null
  }

  return (
    <div
      className={`legacySelfStudyYoutubeMedia${isExpanded ? ' expanded' : ''}`}
      data-qa="legacy-self-study-youtube-player"
      ref={mediaRootRef}
      style={
        {
          '--legacy-self-study-youtube-progress': `${progress}%`,
        } as CSSProperties
      }
    >
      <div className="legacySelfStudyYoutubeFrame">
        <div className="legacySelfStudyYoutubeViewport">
          <iframe
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="legacySelfStudyYoutubeIframe"
            onLoad={handleIframeLoad}
            ref={iframeRef}
            src={embedUrl ?? undefined}
            title={title}
          />
          <LmsButton
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
            className={`legacySelfStudyYoutubeOverlay${isPlaying ? ' playing' : ''}`}
            onClick={handleTogglePlayback}
            size="icon-lg"
            style={{ backgroundImage: isPlaying || !thumbnailUrl ? undefined : `url(${thumbnailUrl})` }}
            type="button"
            variant="ghost"
          >
            {!isPlaying ? <Play aria-hidden="true" /> : null}
          </LmsButton>
        </div>
        <MediaControlBar className="legacySelfStudyYoutubeControls" kind="video">
          <LmsButton
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
            className="lmsMediaControlButton legacySelfStudyYoutubeControl legacySelfStudyYoutubePlayButton"
            onClick={handleTogglePlayback}
            size="icon"
            type="button"
            variant="ghost"
          >
            {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          </LmsButton>
          <input
            aria-label="Video progress"
            className="lmsMediaControlProgress legacySelfStudyYoutubeProgress"
            max={100}
            min={0}
            onChange={(event) => {
              handleSeekPercent(Number(event.currentTarget.value))
            }}
            step={1}
            type="range"
            value={progress}
          />
          <LmsButton
            aria-label={isExpanded ? t('Collapse video') : t('Expand video')}
            className="lmsMediaControlButton legacySelfStudyYoutubeControl legacySelfStudyYoutubeFullscreenButton"
            onClick={handleToggleExpanded}
            size="icon"
            type="button"
            variant="ghost"
          >
            {isExpanded ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
          </LmsButton>
          <LmsButton
            aria-label="Video settings"
            className={`lmsMediaControlButton legacySelfStudyYoutubeControl legacySelfStudyYoutubeSettingsButton${settingsAnchor ? ' active' : ''}`}
            onClick={(event) => {
              setSettingsAnchor(event.currentTarget)
              setSettingsSection('menu')
            }}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Settings aria-hidden="true" />
          </LmsButton>
        </MediaControlBar>
      </div>
      <LegacyYoutubeSettings
        anchorEl={settingsAnchor}
        onChangeSection={setSettingsSection}
        onChangeSpeed={handleChangeSpeed}
        onClose={() => {
          setSettingsAnchor(null)
          setSettingsSection('menu')
        }}
        opensUp={isExpanded}
        playbackRate={playbackRate}
        section={settingsSection}
      />
    </div>
  )
}
